/**
 * Biblioteca de trabalhos: o que o aluno publica, o que fica aberto e quanto
 * custa consultar.
 *
 * Três decisões moram aqui, e elas valem para o site inteiro:
 *
 * 1. **O resumo é de graça, o texto não.** Quem chega na biblioteca lê o resumo,
 *    o abstract e os resumos em outros idiomas. O trabalho completo só abre com a
 *    consulta paga (ou de graça, quando o autor declara que o texto é de IA).
 * 2. **O preço vem do nível, não do autor.** Graduação R$ 30, pós R$ 50,
 *    doutorado R$ 80 — quem publica não escolhe quanto cobrar, para não virar
 *    leilão de TCC. O autor pode pedir **contribuição** por fora da taxa.
 * 3. **O sistema não detecta IA.** Não existe detector confiável, e um falso
 *    positivo aqui acusaria um aluno honesto. O que o sistema faz é: pedir a
 *    declaração do autor, contar o que dá para contar do documento
 *    (`indiciosDeAutoria`) e deixar a decisão com quem modera. Declarar "só IA"
 *    torna o trabalho **gratuito** — não paga nada a ninguém.
 */
import { blocosDoDocumento, textoDoBloco } from './documento/blocos.js';
/* ------------------------------------------------------------------ */
/* Níveis e preço                                                      */
/* ------------------------------------------------------------------ */
export const NIVEIS_DA_BIBLIOTECA = ['GRADUACAO', 'POS', 'DOUTORADO'];
export const ROTULO_NIVEL_DA_BIBLIOTECA = {
    GRADUACAO: 'Graduação',
    POS: 'Pós-graduação (especialização e mestrado)',
    DOUTORADO: 'Doutorado',
};
export const DESCRICAO_NIVEL_DA_BIBLIOTECA = {
    GRADUACAO: 'TCC, monografia de especialização, relatório de estágio e projeto.',
    POS: 'Dissertação de mestrado e monografia de especialização.',
    DOUTORADO: 'Tese de doutorado.',
};
/**
 * O preço da consulta por nível, em centavos.
 *
 * É uma tabela fixa de propósito: preço por trabalho abriria espaço para cobrar
 * caro por texto alheio. Mudar aqui muda para todo mundo, e o preço fica gravado
 * na publicação no momento em que ela é enviada (quem já vendeu não é afetado).
 */
export const PRECO_POR_NIVEL_CENTAVOS = {
    GRADUACAO: 3000,
    POS: 5000,
    DOUTORADO: 8000,
};
export function precoDoNivel(nivel) {
    return PRECO_POR_NIVEL_CENTAVOS[nivel] ?? PRECO_POR_NIVEL_CENTAVOS.GRADUACAO;
}
/**
 * O nível sugerido a partir do tipo declarado no editor.
 *
 * É só sugestão: no ato da publicação o autor confirma, porque "monografia" é
 * usada tanto na graduação quanto na especialização — e o preço muda.
 */
export function nivelDoTipoDeTrabalho(tipo) {
    if (tipo === 'DISSERTACAO')
        return 'POS';
    if (tipo === 'TESE')
        return 'DOUTORADO';
    if (tipo === 'MONOGRAFIA')
        return 'POS';
    return 'GRADUACAO';
}
/** Formata centavos no padrão brasileiro: 3000 → "R$ 30,00". */
export function precoFormatado(centavos) {
    return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
/* ------------------------------------------------------------------ */
/* Declaração de uso de IA                                             */
/* ------------------------------------------------------------------ */
export const USOS_DE_IA = ['NAO', 'PARCIAL', 'TOTAL'];
export const ROTULO_USO_DE_IA = {
    NAO: 'Não usei IA para escrever',
    PARCIAL: 'Usei IA em partes (revisão, resumo, ideias)',
    TOTAL: 'O texto foi gerado por IA',
};
export const DESCRICAO_USO_DE_IA = {
    NAO: 'O texto é seu. É o caso de quem quer publicar e receber pelo acesso.',
    PARCIAL: 'A IA ajudou em partes. O trabalho entra na biblioteca pago, e o moderador pode pedir explicação.',
    TOTAL: 'Texto de IA não tem autoria a vender: o trabalho entra na biblioteca, mas fica **aberto e gratuito** — só o resumo é exibido, sem cobrança de ninguém.',
};
/* ------------------------------------------------------------------ */
/* Estados da publicação                                               */
/* ------------------------------------------------------------------ */
export const ESTADOS_DA_PUBLICACAO = ['EM_ANALISE', 'PUBLICADA', 'RECUSADA', 'ARQUIVADA'];
export const ROTULO_ESTADO_DA_PUBLICACAO = {
    EM_ANALISE: 'Em análise',
    PUBLICADA: 'Publicada',
    RECUSADA: 'Recusada',
    ARQUIVADA: 'Arquivada',
};
/** Só o que está publicado aparece na biblioteca pública. */
export function apareceNaBiblioteca(estado) {
    return estado === 'PUBLICADA';
}
/* ------------------------------------------------------------------ */
/* Contribuição ao autor                                               */
/* ------------------------------------------------------------------ */
/** A menor contribuição que faz sentido cobrar no cartão (R$ 5,00). */
export const CONTRIBUICAO_MINIMA_CENTAVOS = 500;
/** A maior que o checkout aceita sem virar outra conversa (R$ 500,00). */
export const CONTRIBUICAO_MAXIMA_CENTAVOS = 50000;
/** A contribuição pedida, encaixada no que o checkout aceita. */
export function contribuicaoValida(centavos) {
    const numero = Math.round(Number(centavos));
    if (!Number.isFinite(numero) || numero <= 0)
        return 0;
    return Math.min(Math.max(numero, CONTRIBUICAO_MINIMA_CENTAVOS), CONTRIBUICAO_MAXIMA_CENTAVOS);
}
/** O trabalho abre de graça? (declarado como IA, ou liberado pelo moderador) */
export function consultaGratuita(publicacao) {
    return publicacao.gratuito || publicacao.precoCentavos <= 0;
}
/** Quanto custa consultar agora. */
export function precoDaConsulta(publicacao) {
    return consultaGratuita(publicacao) ? 0 : publicacao.precoCentavos;
}
/** Como o preço aparece na vitrine da biblioteca. */
export function rotuloDoPreco(publicacao) {
    if (consultaGratuita(publicacao))
        return 'Aberto — sem cobrança';
    return `${precoFormatado(publicacao.precoCentavos)} para consultar`;
}
export function indiciosDeAutoria(documento) {
    const blocos = blocosDoDocumento(documento);
    const palavras = blocos
        .map((bloco) => textoDoBloco(bloco))
        .join(' ')
        .split(/\s+/)
        .filter(Boolean).length;
    const secoes = documento.secoes.length;
    const referencias = documento.referencias.length;
    return {
        secoes,
        palavras,
        figuras: documento.figuras.length + blocos.filter((bloco) => bloco.tipo === 'IMAGEM').length,
        tabelas: blocos.filter((bloco) => bloco.tipo === 'TABELA').length,
        citacoesLongas: blocos.filter((bloco) => bloco.tipo === 'CITACAO').length,
        referencias,
        referenciasPorSecao: secoes > 0 ? Number((referencias / secoes).toFixed(2)) : 0,
        temCapa: Boolean(documento.capa?.url),
        temCabecalho: Boolean(documento.cabecalho?.url),
        temResumo: documento.resumo.trim().length > 0,
        temAbstract: documento.abstract.trim().length > 0,
        resumosEmOutrosIdiomas: documento.resumos?.length ?? 0,
        tamanhoMedioDaSecao: secoes > 0 ? Math.round(palavras / secoes) : 0,
    };
}
/**
 * As linhas da ficha de indícios, prontas para a tela.
 *
 * A ordem não é arbitrária: referências primeiro, porque trabalho acadêmico sem
 * referência é o problema mais comum e o mais fácil de ver.
 */
export function linhasDosIndicios(indicios) {
    return [
        `${indicios.referencias} referência(s) para ${indicios.secoes} seção(ões)`,
        `${indicios.palavras.toLocaleString('pt-BR')} palavras no texto`,
        `${indicios.figuras} figura(s) e ${indicios.tabelas} tabela(s)`,
        `${indicios.citacoesLongas} citação(ões) longa(s)`,
        indicios.temCapa ? 'capa com imagem' : 'capa sem imagem',
        indicios.temResumo ? 'resumo escrito' : 'sem resumo',
        `${indicios.resumosEmOutrosIdiomas} resumo(s) em outro idioma`,
    ];
}
/**
 * O texto que o leitor recebe ao consultar — o trabalho inteiro, em Markdown.
 *
 * Fica no core porque as duas portas (a tela e um eventual download) têm de
 * devolver a mesma coisa.
 */
export function publicacaoSemTexto(publicacao) {
    return `O texto completo de “${publicacao.titulo}” abre com a consulta.`;
}
//# sourceMappingURL=biblioteca.js.map